import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { NewPartnerForm } from "../../forms/NewPartnerForm/NewPartnerForm";
import {
  DocumentOfReceive,
  ReceiveStatus,
} from "../../models/DocumentOfReceive";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { RoleType } from "../../models/Role";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { getRepromaterialOutputTemplate } from "../../document-templates/RepromaterialOutputTemplate";
import { activeUser } from "../../services/AuthService";
import {
  getOneDocument,
  addNewDocument,
  updateOneDocument,
  getOneDisposition,
  getAllDocuments,
  getProductReservations,
  getOnHand,
} from "../../services/NewVersionService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { getAllPartners } from "../../services/PartnerService";
import "./NewDocumentOfOutputRepromaterialPage.scss";

export interface DocumentOfOutputPageProps {
  show?: string;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewPartnerStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
}

export const NewDocumentOfOutputRepromaterialPage = (
  p: DocumentOfOutputPageProps,
) => {
  const { show, buttonSaveStyle, buttonNewPartnerStyle, buttonResetStyle } = p;

  const { id } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const isNew = !id || id === "new";

  const unwrapArray = (resp: any): any[] => {
    const d = resp?.data;
    if (d?.result && Array.isArray(d.result)) return d.result;
    if (Array.isArray(d)) return d;
    return [];
  };

  const buildDefaultDocument = (docId?: string): DocumentOfReceive => ({
    id: docId ?? "",
    warehouse:
      activeUser?.role?.name === RoleType.Warehouseman
        ? (cloneDeep(activeUser.warehouse) as Warehouse)
        : ({} as Warehouse),
    warehouseman: `${activeUser?.firstName} ${activeUser?.lastName}`,
    type: undefined,
    date: new Date(),
    time: new Date(),
    repromaterialProducts: [],
    repromaterialProductsJSON: "",
    partner: {} as Partner,
    shipper: {} as Partner,
    driverName: "",
    vehicleNumber: "",
    note: "",
    dispositionId: "",
    finishedDocument: false,
    finishedDocumentFirstTime: false,
    foreignProduct: false,
    productOwner: {} as Partner,
    mercantile: false,
    input: false,
  });

  const [updating, setUpdating] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>(
    [],
  );
  const [products, setProducts] = useState<Product[]>([]);

  const [newDocumentOfOutput, setNewDocumentOfOutput] =
    useState<DocumentOfReceive>(() => buildDefaultDocument(isNew ? "" : id));

  const [newProduct, setNewProduct] = useState<Product>({
    id: "",
    name: "",
    type: ProductType.RawMaterial,
    code: "",
    category: {} as ProductCategory,
    lot: "",
    amount: "",
    measuringUnit: "",
    germination: "",
    activeSubstance: "",
    expiryDate: undefined,
  });
  const [reproInputsApi, setReproInputsApi] = useState<any[]>([]);
  const [reproOutputsApi, setReproOutputsApi] = useState<any[]>([]);
  const [productReservationsApi, setProductReservationsApi] = useState<any[]>(
    [],
  );
  const [maxAmount, setMaxAmount] = useState<number>(0); //maksimalno dostupna kolicina za selektovani magacin i selektovanu robu
  const [onHandNew, setOnHandNew] = useState<number>(0);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

  const [productsDataTable, setProductsDataTable] = useState<Product[]>([]);
  const [okButtonDisabled, setOkButtonDisabled] = useState<boolean>(false);
  const [editingProduct, setEditingProduct] = useState<boolean>(false);

  useEffect(() => {
    //create all products table
    let newProductsDataTable: any[] = [];
    if (newDocumentOfOutput.repromaterialProducts) {
      const allProducts = newDocumentOfOutput.repromaterialProducts ?? [];
      for (const productItem of allProducts) {
        const product = products.find((item) => item.id === productItem.id);
        newProductsDataTable.push({
          id: product?.id ?? "",
          type: ProductType.RawMaterial,
          name: products.find((item) => item.id === product?.id)
            ?.name as string,
          code: products.find((item) => item.id === product?.id)
            ?.name as string,
          category: product?.category as ProductCategory,
          lot: productItem?.lot ?? "",
          amount: productItem?.amount ?? "",
          measuringUnit: product?.measuringUnit ?? "",
          germination: productItem?.germination ?? "",
          activeSubstance: productItem?.activeSubstance ?? "",
          expiryDate: new Date(productItem?.expiryDate as Date) ?? undefined,
        });
      }
    }
    setProductsDataTable(newProductsDataTable);
  }, [newDocumentOfOutput.repromaterialProducts, products]);

  const [inputBasedOnDisposition, setInputBasedOnDisposition] =
    useState<boolean>(false);

  const [pdfData, setPdfData] = useState<any>(null);

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  useEffect(() => {
    const loadBasicData = async () => {
      try {
        setIsLoading(true);

        const [whResp, partnersResp, catResp, prodResp] = await Promise.all([
          getAllWarehouses(),
          getAllPartners(),
          getAllProductCategories(),
          getAllProducts(),
        ]);

        const wh = unwrapArray(whResp) as Warehouse[];
        const pa = unwrapArray(partnersResp) as Partner[];
        const ca = unwrapArray(catResp) as ProductCategory[];
        const pr = unwrapArray(prodResp) as Product[];

        setWarehouses(wh);
        setPartners(pa);
        setProductCategories(ca);
        setProducts(pr);

        setFilteredProducts(
          pr.filter((x) => x.type === ProductType.RawMaterial),
        );

        // NEW dokument: odmah postavi pdf preview
        if (isNew) {
          const d = buildDefaultDocument("");
          setNewDocumentOfOutput(d);
          setPdfData(getRepromaterialOutputTemplate(d, pr));
          setUpdating(false);
          setInputBasedOnDisposition(false);
          return;
        }

        // EDIT: ucitaj dokument iz API
        const resp = await getOneDocument(id as string);
        const document: any = resp?.data?.result ?? resp?.data;

        if (document?.date) document.date = new Date(document?.date);
        if (document?.time) document.time = new Date(document?.time);
        if (!document?.warehouseman)
          document.warehouseman = `${activeUser.firstName} ${activeUser.lastName}`;
        if (
          document?.dispositionId &&
          document?.status !== ReceiveStatus.Arrived
        ) {
          setInputBasedOnDisposition(true);
          document.status = ReceiveStatus.Arrived;

          try {
            const oneDisposition = await getOneDisposition(
              document.dispositionId,
            );
            const allProductsJson = cloneDeep(
              oneDisposition.data.result
                ?.repromaterialDispositionProductsJSON as string,
            );
            const allProducts = JSON.parse(allProductsJson);
            const repromaterialProducts = allProducts
              .filter((productItem: any) => parseFloat(productItem?.amount) > 0)
              .map((productItem: any) => ({
                id: productItem?.id ?? "",
                type: ProductType.RawMaterial,
                name:
                  pr.find((item) => item.id === productItem?.id)?.name ?? "",
                code:
                  pr.find((item) => item.id === productItem?.id)?.name ?? "",
                category: productItem?.category as ProductCategory,
                lot: "",
                amount: productItem?.amount ?? "",
                measuringUnit: productItem?.measuringUnit ?? "",
                germination: productItem?.germination ?? "",
                activeSubstance: productItem?.activeSubstance ?? "",
                expiryDate: productItem?.expiryDate ?? undefined,
              }));

            document.repromaterialProductsJSON = allProductsJson;
            document.repromaterialProducts = repromaterialProducts;
          } catch (err) {
            console.error("Greška pri učitavanju dispozicija:", err);
          }
        }
        if (
          !document.repromaterialProducts &&
          document.repromaterialProductsJSON
        ) {
          try {
            document.repromaterialProducts = JSON.parse(
              document.repromaterialProductsJSON,
            );
          } catch (err) {
            console.error("Greška u parsiranju repromaterialProductsJSON", err);
          }
        }

        setNewDocumentOfOutput(document);
        const newPdf = getRepromaterialOutputTemplate(document, pr);
        setPdfData(newPdf);
        setUpdating(true);
      } catch (error) {
        console.error("Greška pri učitavanju podataka:", error);
      } finally {
        setIsLoading(false);
      }
    };
    const loadSecondaryData = async () => {
      try {
        const currentYear = new Date().getFullYear();
        const dateFrom = new Date(currentYear, 0, 1, 0, 0, 1); // 1.1. tekuće godine
        const dateTo = new Date(); // danas

        const isWarehouseman = activeUser?.role?.name === RoleType.Warehouseman;
        const warehouseId = isWarehouseman
          ? (activeUser?.warehouse?.id as string)
          : "";

        const [inputsResponse, outputsResponse, reservationsResponse] =
          await Promise.all([
            // repromaterijal – PRIJEMNICE
            getAllDocuments(
              false, // mercantile = false → repromaterijal
              true, // input = true   → ulaz
              dateFrom,
              dateTo,
              isWarehouseman,
              warehouseId,
            ),
            // repromaterijal – OTPREMNICE
            getAllDocuments(
              false, // mercantile = false → repromaterijal
              false, // input = false → izlaz
              dateFrom,
              dateTo,
              isWarehouseman,
              warehouseId,
            ),
            // rezervacije repromaterijala
            getProductReservations(currentYear, dateFrom, dateTo),
          ]);

        setReproInputsApi(inputsResponse?.data?.result ?? []);
        setReproOutputsApi(outputsResponse?.data?.result ?? []);
        setProductReservationsApi(reservationsResponse?.data?.result ?? []);
      } catch (error) {
        console.error("Greška pri učitavanju sekundarnih podataka:", error);
      }
    };

    loadBasicData();
    loadSecondaryData();
  }, []);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getRepromaterialOutputTemplate(
      newDocumentOfOutput,
      products,
    );
    setPdfData(newPdf);
  }, [newDocumentOfOutput, products]);

  useEffect(() => {
    if (newProduct.category?.id === undefined) {
      setFilteredProducts(
        products.filter((x) => x.type === ProductType.RawMaterial),
      );
    } else {
      setFilteredProducts(
        products.filter((x) => x.category?.id == newProduct.category?.id),
      );
    }
  }, [newProduct]);

  useEffect(() => {
    if (
      newDocumentOfOutput.warehouse?.name !== undefined &&
      newProduct?.name !== undefined
    ) {
      const additionalAmount =
        newProduct?.amount && newProduct.amount !== ""
          ? parseFloat(newProduct.amount.toString())
          : 0;

      calculateMaxAmount(additionalAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    newDocumentOfOutput.warehouse,
    newProduct,
    reproInputsApi,
    reproOutputsApi,
    productReservationsApi,
  ]);

  useEffect(() => {
    const run = async () => {
      const warehouseId = newDocumentOfOutput?.warehouse?.id;
      const productId = newProduct?.id;
      const year = new Date().getFullYear();

      if (!warehouseId || !productId) {
        setOnHandNew(0);
        return;
      }

      try {
        const resp = await getOnHand({ year, warehouseId, productId });
        const v = Number(resp?.data?.result?.onHand ?? 0);
        setOnHandNew(Number(v.toFixed(3)));
        console.log(`[STOCK_BALANCE][REPRO][DIRECT_OUTPUT][ON_HAND]`, {
          year,
          warehouseId,
          productId,
          onHand: v,
        });
      } catch (e) {
        console.log("[STOCK_BALANCE][REPRO][DIRECT_OUTPUT][ON_HAND] error", e);
        setOnHandNew(0);
      }
    };

    run();
  }, [newDocumentOfOutput?.warehouse?.id, newProduct?.id]);
  console.log(onHandNew);

  const toast = useRef(null);

  const calculateMaxAmount = (additionalAmount: number) => {
    //additionalAmount se dodaje kod editovanja otpremnica, dostupna kolicina se uvecava za iznos robe po otpremnici
    let newMaxAmount: number =
      editingProduct &&
      (newDocumentOfOutput.dispositionId !== "" ||
        newDocumentOfOutput.finishedDocument === true)
        ? additionalAmount
        : 0;
    const selectedWarehouseName = newDocumentOfOutput.warehouse?.name;
    const selectedProductId = newProduct?.id;
    if (!selectedWarehouseName || !selectedProductId) {
      setMaxAmount(Number(newMaxAmount.toFixed(3)));
      return;
    }
    // 1) Ulazi repromaterijala (prijemnice)
    for (const repromaterialInput of reproInputsApi) {
      if (repromaterialInput.warehouseName !== selectedWarehouseName) continue;

      const productIds = (repromaterialInput.reproProducts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      const amountsRaw = (repromaterialInput.reproAmounts ?? "")
        .split(";")
        .map((x: string) => x.trim());

      for (let i = 0; i < productIds.length; i++) {
        const prodId = productIds[i];
        if (prodId !== selectedProductId) continue;

        const raw = amountsRaw[i] ?? "0";
        // primer: "10kg" → 10
        const amount = parseFloat(raw) || 0;
        newMaxAmount += amount;
      }
    }

    // 2) Izlazi repromaterijala (otpremnice) – svi izlazi utiču na stanje (reproOutputsApi)
    for (const repromaterialOutput of reproOutputsApi) {
      if (repromaterialOutput.warehouseName !== selectedWarehouseName) continue;

      const productIds = (repromaterialOutput.reproProducts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      const amountsRaw = (repromaterialOutput.reproAmounts ?? "")
        .split(";")
        .map((x: string) => x.trim());

      for (let i = 0; i < productIds.length; i++) {
        const prodId = productIds[i];
        if (prodId !== selectedProductId) continue;

        const raw = amountsRaw[i] ?? "0";
        const amount = parseFloat(raw) || 0;
        newMaxAmount -= amount;
      }
    }

    // 3) Rezervacije repromaterijala (kod zahteva premestaja gde je poznat magacina iz dispozicije)
    for (const productReservation of productReservationsApi) {
      if (productReservation.type !== ProductType.RawMaterial) continue;

      if (
        productReservation.warehouse?.name === selectedWarehouseName &&
        new Date(productReservation?.expiryDate as Date).getTime() >
          new Date().getTime()
      ) {
        const reservationProductId = productReservation.product?.id;
        if (!reservationProductId) continue;

        if (reservationProductId === selectedProductId) {
          const amount = Number(productReservation?.amount ?? 0);
          newMaxAmount -= amount;
        }
      }
    }

    setMaxAmount(Number(newMaxAmount.toFixed(3)));
  };

  const handleNewDocumentOfOutput = (prop: string, value: any) => {
    setNewDocumentOfOutput((newDocumentOfOutput) => ({
      ...newDocumentOfOutput,
      [prop]: value,
    }));
  };

  const handleNewRegistrationNumber = (prop: string, value: any) => {
    setNewDocumentOfOutput((newDocumentOfOutput) => ({
      ...newDocumentOfOutput,
      [prop]: value.toUpperCase(),
    }));
  };

  const handlePartnerChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
    copyOfNewDocumentOfOutput.partner = partners.find(
      (item) => item.id == value,
    );
    setNewDocumentOfOutput(copyOfNewDocumentOfOutput);
  };

  const handleProductOwnerChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
    copyOfNewDocumentOfOutput.productOwner = partners.find(
      (item) => item.id == value,
    );
    setNewDocumentOfOutput(copyOfNewDocumentOfOutput);
  };

  const handleShipperChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
    copyOfNewDocumentOfOutput.shipper = partners.find(
      (item) => item.id == value,
    );
    setNewDocumentOfOutput(copyOfNewDocumentOfOutput);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
    activeUser?.role?.name === RoleType.Warehouseman
      ? (copyOfNewDocumentOfOutput.warehouse = cloneDeep(
          activeUser.warehouse,
        ) as Warehouse)
      : (copyOfNewDocumentOfOutput.warehouse = warehouses.find(
          (item) => item.id == value,
        ) as Warehouse);
    setNewDocumentOfOutput(copyOfNewDocumentOfOutput);
  };

  const handleNewProduct = (prop: string, value: any) => {
    setNewProduct((newProduct) => ({
      ...newProduct,
      [prop]: value,
    }));
  };

  const handleCategoryChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(newProduct);
    copyOfNewProduct.category = productCategories.find(
      (category) => category.id === value,
    );
    setNewProduct(copyOfNewProduct);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(newProduct);
    copyOfNewProduct.id = value;
    copyOfNewProduct.name = value;
    copyOfNewProduct.measuringUnit = filteredProducts.find(
      (product) => product.id === value,
    )?.measuringUnit;
    if (newProduct.category?.id === undefined) {
      copyOfNewProduct.category = filteredProducts.find(
        (product) => product.id === value,
      )?.category;
    }
    setNewProduct(copyOfNewProduct);
  };

  const addNewProduct = () => {
    if (maxAmount < (parseFloat(newProduct.amount as string) ?? 0)) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        summary: t(`Neodgovarajuća količina`),
        detail: t(
          `Uneta količina proizvoda je veća od maksimalno dostupne količine u selektovanom magacinu. Maksimalno dostupna količina za izabrani proizvod je ${maxAmount}`,
        ),
        sticky: true,
      });
      return;
    }
    if (newProduct.name === "" || newProduct.amount === "") {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(`Moraju biti popunjena polja za naziv i količinu proizvoda`),
        life: 3000,
      });
      return;
    }

    if ((newProduct.amount as string).indexOf(",") >= 0) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(
          `Neispravan unos količine. Decimalni broj treba uneti sa . a ne sa ,`,
        ),
        life: 3000,
      });
      return;
    }

    if (!/^-?\d+(\.\d+)?$/.test(newProduct.amount as string)) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(`Količina uneta u neispravnom formatu`),
        life: 3000,
      });
      return;
    }

    const productAdded = newDocumentOfOutput.repromaterialProducts?.find(
      (item) => item.id == newProduct.id /* && item?.lot == newProduct?.lot*/,
    );
    if (!productAdded) {
      //add product to allProductList
      let copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
      let copyOfNewProduct = cloneDeep(newProduct);
      copyOfNewDocumentOfOutput.repromaterialProducts?.push(copyOfNewProduct);
      setNewDocumentOfOutput(copyOfNewDocumentOfOutput);
    } else {
      //update existing product
      let copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
      newDocumentOfOutput.repromaterialProducts?.map((product, index) => {
        if (
          product.id === newProduct.id /* && product?.lot === newProduct?.lot*/
        )
          //@ts-ignore
          copyOfNewDocumentOfOutput.repromaterialProducts[index] =
            cloneDeep(newProduct);
      });
      setNewDocumentOfOutput(copyOfNewDocumentOfOutput);
    }

    setFilteredProducts(
      products.filter((item) => item.type === ProductType.RawMaterial),
    );

    //reset newProductFields
    setNewProduct({
      id: "",
      name: "",
      type: ProductType.RawMaterial,
      code: "",
      category: {} as ProductCategory,
      lot: "",
      amount: "",
      measuringUnit: "",
      germination: "",
      activeSubstance: "",
      expiryDate: undefined,
    });
    setEditingProduct(false);
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  const onSubmit = () => {
    if (newDocumentOfOutput.repromaterialProducts?.length === 0) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(
          `Nije moguće snimiti otpremnicu. Nije unet ni jedan proizvod.`,
        ),
        life: 3000,
      });
      return;
    }
    setOkButtonDisabled(true);
    const copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
    copyOfNewDocumentOfOutput.repromaterialProductsJSON = JSON.stringify(
      newDocumentOfOutput.repromaterialProducts,
    );

    if (newDocumentOfOutput?.finishedDocumentFirstTime !== true) {
      copyOfNewDocumentOfOutput.date = new Date(); //ako nije zakljucena otpremnica prvi put,
      copyOfNewDocumentOfOutput.time = new Date(); //datum i vreme se formiraju kao trenutak zakljucenja otpremnice prvi put
    }

    if (newDocumentOfOutput.finishedDocument === true) {
      copyOfNewDocumentOfOutput.finishedDocumentFirstTime = true; //zakljucena otpremnica prvi put
    }

    if (!copyOfNewDocumentOfOutput.warehouseman) {
      copyOfNewDocumentOfOutput.warehouseman = `${activeUser?.firstName} ${activeUser?.lastName}`;
    }

    if (updating) {
      updateOneDocument(id as string, copyOfNewDocumentOfOutput).then(
        (response: AxiosResponse) => {
          if (response.status === 200 || response.status === 201) {
            //@ts-ignore
            toast.current.show({
              severity: "success",
              detail: t(`Uspešno popunjena otpremnica`),
              life: 3000,
            });
            navigate(`/repromaterial-output-fast`);
            setOkButtonDisabled(false);
          } else if (response.status === 422) {
            //@ts-ignore
            toast.current.show({
              severity: "error",
              detail: t(
                `Nema dovoljne količine svih proizvoda u izabranom magacinu (u toku izrade otpremnice neko od kolega je izvršio rezervaciju robe). Proverite dostupne količine`,
              ),
              sticky: true,
            });
            setOkButtonDisabled(false);
          } else {
            //@ts-ignore
            toast.current.show({
              severity: "error",
              detail: t(
                `Dokument nije snimljen, proverite podatke o otpremi robe`,
              ),
              life: 3000,
            });
            setOkButtonDisabled(false);
          }
        },
      );
    } else {
      const payload: any = { ...cloneDeep(copyOfNewDocumentOfOutput) };
      if (!payload.id || payload.id.trim() === "") delete payload.id;
      addNewDocument(payload).then((response: AxiosResponse) => {
        if (response.status === 200 || response.status === 201) {
          //@ts-ignore
          toast.current.show({
            severity: "success",
            detail: t(`Uspešno izmenjena otpremnica`),
            life: 3000,
          });
          navigate(`/repromaterial-output-fast`);
          setOkButtonDisabled(false);
        } else if (response.status === 422) {
          //@ts-ignore
          toast.current.show({
            severity: "error",
            detail: t(
              `Nema dovoljne količine svih proizvoda u izabranom magacinu (u toku izrade otpremnice neko od kolega je izvršio rezervaciju robe). Proverite dostupne količine`,
            ),
            sticky: true,
          });
          setOkButtonDisabled(false);
        } else {
          //@ts-ignore
          toast.current.show({
            severity: "error",
            detail: t(
              `Dokument nije izmenjen, proverite podatke o otpremi robe`,
            ),
            life: 3000,
          });
          setOkButtonDisabled(false);
        }
      });
    }
  };

  const onError = () => {
    setOkButtonDisabled(true);
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
    setOkButtonDisabled(false);
  };

  const handleResetForm = () => {
    setNewDocumentOfOutput({
      ...newDocumentOfOutput,
      warehouse:
        activeUser?.role?.name === RoleType.Warehouseman
          ? (cloneDeep(activeUser.warehouse) as Warehouse)
          : ({} as Warehouse),
      warehouseman: `${activeUser?.firstName} ${activeUser?.lastName}`,
      type: undefined,
      date: new Date(),
      time: new Date(),
      repromaterialProducts: [],
      repromaterialProductsJSON: "",
      partner: {} as Partner,
      shipper: {} as Partner,
      driverName: "",
      vehicleNumber: "",
      loadingCityName: "",
      note: "",
      finishedDocument: false,
      foreignProduct: false,
      productOwner: {} as Partner,
      mercantile: false,
      input: false,
    });
  };

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
    addButtonSaveStyle.margin = "0 .5rem";
  }

  let addButtonNewPartnerStyle: React.CSSProperties = {};
  if (buttonNewPartnerStyle) {
    addButtonNewPartnerStyle = buttonNewPartnerStyle;
  } else {
    addButtonNewPartnerStyle.background = "rgba(21, 115, 71, 1)";
    addButtonNewPartnerStyle.border = "none";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  const nameOfProduct = (rowData: any) => {
    return rowData?.name;
  };

  const nameOfCategory = (rowData: any) => {
    return rowData.category?.name;
  };

  const editActionBodyTemplate = (rowData: any) => {
    return (
      <ButtonInput
        labelText=""
        icon="bi bi-pencil"
        onButtonClick={() => {
          setEditingProduct(true);
          setNewProduct(rowData);
        }}
        addStyle={{
          backgroundColor: "white",
          color: "rgba(21, 115, 71, 1)",
          border: "none",
        }}
      />
    );
  };

  if (isLoading) return <ProgressSpinner />;

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-truck header-title-icon"
              style={{ fontSize: 18 }}
            ></i>
            <span className="header-title">
              {`${t("Otpremnica broj")}:`}{" "}
              {newDocumentOfOutput?.finishedDocumentNumber ??
                newDocumentOfOutput.finishedDocumentId ??
                ""}
            </span>
          </div>
          <div className="header-buttons">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={t("Sve otpremnice")}
              icon="bi bi-arrow-left-square"
              onButtonClick={() => {
                navigate(`/repromaterial-output-fast`);
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (
                  newDocumentOfOutput.finishedDocument === true &&
                  newDocumentOfOutput.finishedDocumentId &&
                  newDocumentOfOutput.repromaterialProducts &&
                  newDocumentOfOutput.repromaterialProducts.length > 0 &&
                  newDocumentOfOutput.driverName !== "" &&
                  newDocumentOfOutput.vehicleNumber !== "" &&
                  newDocumentOfOutput.warehouseman !== ""
                ) {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće preuzeti otpremnicu u kojoj nisu uneta sva obavezna polja, koja ne sadrži ni jedan proizvod u listi, koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (
                  newDocumentOfOutput.finishedDocument === true &&
                  newDocumentOfOutput.finishedDocumentId &&
                  newDocumentOfOutput.repromaterialProducts &&
                  newDocumentOfOutput.repromaterialProducts.length > 0 &&
                  newDocumentOfOutput.driverName !== "" &&
                  newDocumentOfOutput.vehicleNumber !== "" &&
                  newDocumentOfOutput.warehouseman !== ""
                ) {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće odštampati otpremnicu u kojoj nisu uneta sva obavezna polja, koja ne sadrži ni jedan proizvod u listi, koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
          </div>
        </div>
        <div className="form-body">
          <div className="form-input-fields">
            {activeUser?.role?.name === RoleType.Warehouseman ? (
              <TextInput
                id="warehouse"
                value={activeUser?.warehouse?.name ?? ""}
                onValueChange={handleWarehouseChange}
                labelText={`${t("Magacin")} *`}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman ||
                  newDocumentOfOutput.finishedDocument === true
                }
              />
            ) : (
              <DropdownInput
                id="warehouse"
                value={newDocumentOfOutput?.warehouse?.id}
                onValueChange={handleWarehouseChange}
                optionValue="id"
                optionLabel="name"
                options={warehouses.filter((item) => item.virtual !== true)}
                filter={false}
                labelText={`${t("Magacin")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  //@ts-ignore
                  activeUser?.role?.name === RoleType.Warehouseman ||
                  newDocumentOfOutput.finishedDocument === true
                }
              />
            )}
            <DateInput
              id="date"
              value={
                newDocumentOfOutput?.finishedDocumentFirstTime
                  ? (newDocumentOfOutput.date as Date)
                  : new Date()
              }
              onValueChange={handleNewDocumentOfOutput}
              labelText={`${t("Datum")} *`}
              placeholderText={t("Izaberite datum")}
              icon="bi bi-calendar-check"
              maxDate={new Date()}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman ||
                newDocumentOfOutput.finishedDocument === true
              }
            />
            <TextInput
              id="warehouseman"
              value={newDocumentOfOutput.warehouseman as string}
              onValueChange={handleNewDocumentOfOutput}
              labelText={`${t("Magacioner")} *`}
              placeholderText={t("Unesite ime magacionera")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: false }}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman &&
                newDocumentOfOutput.finishedDocument === true
              }
            />
            <DateInput
              id="time"
              value={
                newDocumentOfOutput?.finishedDocumentFirstTime
                  ? (newDocumentOfOutput.time as Date)
                  : new Date()
              }
              onValueChange={handleNewDocumentOfOutput}
              labelText={`${t("Vreme")} *`}
              placeholderText={t("Izaberite vreme")}
              timeOnly={true}
              icon="bi bi-clock"
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman &&
                newDocumentOfOutput.finishedDocument === true
              }
            />
            <div className="divider-line">
              <Divider />
            </div>
            <DropdownInput
              id="partner"
              value={newDocumentOfOutput.partner?.id as string}
              optionValue="id"
              optionLabel="name"
              onValueChange={handlePartnerChange}
              options={partners}
              filter={true}
              labelText={`${t("Kupac")} *`}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman &&
                newDocumentOfOutput.finishedDocument === true
              }
            />
            <div className="new-partner-button">
              <NewPartnerForm
                buttonLabel={`${t("Dodaj kupca")}`}
                buttonIcon="bi bi-plus-square"
                show={show}
                buttonStyle={{
                  border: "none",
                  background: "rgba(21, 115, 71, 1)",
                  color: "#ffffff",
                  marginRight: "1rem",
                  margin: "0 1rem 0 0",
                }}
                buttonNewCityStyle={{
                  border: "none",
                  background: "rgba(21, 115, 71, 1)",
                }}
                buttonSaveStyle={{
                  border: "none",
                  background: "rgba(0, 165, 83, 1)",
                }}
                buttonInactiveStyle={{
                  border: "none",
                  background: "rgba(108, 117, 125, 1)",
                }}
              />
            </div>
            <DropdownInput
              id="shipper"
              value={newDocumentOfOutput.shipper?.id as string}
              optionValue="id"
              optionLabel="name"
              onValueChange={handleShipperChange}
              options={partners}
              filter={true}
              labelText={t("Prevoznik")}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              //rules={{ required: true }}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman &&
                newDocumentOfOutput.finishedDocument === true
              }
            />
            <TextInput
              id="driverName"
              value={newDocumentOfOutput.driverName as string}
              onValueChange={handleNewDocumentOfOutput}
              labelText={`${t("Ime vozača")} *`}
              placeholderText={t("Unesite ime vozača")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman &&
                newDocumentOfOutput.finishedDocument === true
              }
            />
            <TextInput
              id="vehicleNumber"
              value={(
                newDocumentOfOutput.vehicleNumber as string
              )?.toUpperCase()}
              onValueChange={handleNewRegistrationNumber}
              labelText={`${t("Registarski broj vozila")} *`}
              placeholderText={t("Unesite registarski broj")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{
                required: true,
                //pattern: {
                //    value: /^[A-Z]{2}\s([0-9]{3,4}[A-Z]{2}|[A-Z]{3}[0-9]{2})$/,
                //    message: 'Unesite registarski broj u ispravnom formatu',
                //}
              }}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman &&
                newDocumentOfOutput.finishedDocument === true
              }
            />
            <TextInput
              id="loadingCityName"
              value={newDocumentOfOutput.loadingCityName as string}
              onValueChange={handleNewDocumentOfOutput}
              labelText={`${t("Mesto istovara")}`}
              placeholderText={t("Unesite mesto istovara")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman &&
                newDocumentOfOutput?.status === ReceiveStatus.Finished
              }
            />
            <TextInput
              id="loadingContact"
              value={newDocumentOfOutput.loadingContact as string}
              onValueChange={handleNewDocumentOfOutput}
              labelText={`${t("Kontakt na istovaru")}`}
              placeholderText={t("Unesite kontakt na istovaru")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman &&
                newDocumentOfOutput?.status === ReceiveStatus.Finished
              }
            />
            <div className="empty-field"></div>
            <div className="divider-line">
              <Divider />
            </div>
            <>
              <DropdownInput
                id="category"
                value={newProduct.category?.id}
                onValueChange={handleCategoryChange}
                optionValue="id"
                optionLabel="name"
                options={productCategories.filter(
                  (x) => x.type == ProductType.RawMaterial,
                )}
                labelText={t("Tip proizvoda")}
                placeholderText={t("Izaberite tip proizvoda")}
                control={control}
                setValue={setValue}
                errors={errors}
                //rules={{ required: true }}
                disabled={
                  (inputBasedOnDisposition === true ||
                    newDocumentOfOutput.status === ReceiveStatus.Arrived) &&
                  !(
                    activeUser?.role?.name === RoleType.Administrator ||
                    activeUser?.role?.name === RoleType.Superadmin
                  )
                }
              />
              <DropdownInput
                id="name"
                value={newProduct.id}
                optionValue="id"
                optionLabel="name"
                onValueChange={handleProductChange}
                options={filteredProducts}
                filter={true}
                labelText={t("Proizvod")}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                //rules={{ required: true }}
                disabled={
                  (inputBasedOnDisposition === true ||
                    newDocumentOfOutput.status === ReceiveStatus.Arrived) &&
                  !(
                    activeUser?.role?.name === RoleType.Administrator ||
                    activeUser?.role?.name === RoleType.Superadmin
                  )
                }
              />
              <div className="repro-product-details">
                {(newProduct.category?.name == "SAAT - Seme" ||
                  newProduct.category?.name == "PFLA - Hemija") && (
                  <TextInput
                    id="lot"
                    value={newProduct.lot as string}
                    onValueChange={handleNewProduct}
                    labelText={t("Partija (LOT)")}
                    placeholderText=""
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={
                      activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfOutput.finishedDocument === true
                    }
                  />
                )}
                {newProduct.category?.name == "SAAT - Seme" && (
                  <TextInput
                    id="germination"
                    value={newProduct.germination as string}
                    onValueChange={handleNewProduct}
                    labelText={t("Klijavost")}
                    placeholderText=""
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={
                      activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfOutput.finishedDocument === true
                    }
                  />
                )}
                {(newProduct.category?.name == "DUMI - Djubriva" ||
                  newProduct.category?.name == "PFLA - Hemija") && (
                  <TextInput
                    id="activeSubstance"
                    value={newProduct.activeSubstance as string}
                    onValueChange={handleNewProduct}
                    labelText={t("Aktivna supstanca")}
                    placeholderText=""
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={
                      activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfOutput.finishedDocument === true
                    }
                  />
                )}
                {newProduct.category?.name == "PFLA - Hemija" && (
                  <DateInput
                    id="expiryDate"
                    value={
                      newProduct.expiryDate &&
                      new Date(newProduct.expiryDate).getTime() > 1
                        ? (newProduct.expiryDate as Date)
                        : null
                    }
                    onValueChange={handleNewProduct}
                    labelText={`${t("Datum isticanja")}`}
                    placeholderText=""
                    icon="bi bi-calendar-check"
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={
                      activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfOutput.finishedDocument === true
                    }
                  />
                )}
                {newProduct.category?.name == "SAAT - Seme" && (
                  <div className="empty-field"></div>
                )}
                {newProduct.category?.name == "DUMI - Djubriva" && (
                  <>
                    <div className="empty-field"></div>
                    <div className="empty-field"></div>
                  </>
                )}
                {(newProduct.category?.name == undefined ||
                  newProduct.category?.name == "HILF - Pomocni materijal" ||
                  newProduct.category?.name == "AGCM - Merkantila") && (
                  <>
                    <div className="empty-field"></div>
                    <div className="empty-field"></div>
                    <div className="empty-field"></div>
                  </>
                )}
                <TextInput
                  id="amount"
                  value={newProduct.amount as string}
                  onValueChange={handleNewProduct}
                  labelText={`${t("Količina")} (lager: ${maxAmount})`}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    //required: true,
                    pattern: {
                      value: /\d+\.?\d*/,
                      message: "Please enter a number",
                    },
                  }}
                  disabled={
                    activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfOutput.finishedDocument === true
                  }
                />
                <TextInput
                  id="measuringUnit"
                  value={newProduct.measuringUnit as string}
                  onValueChange={handleNewProduct}
                  labelText={t("Jedinica mere")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  //rules={{ required: true }}
                  disabled={true}
                />
                <div className="add-product">
                  <ButtonInput
                    labelText={t("Dodaj proizvod")}
                    icon="bi bi-plus-square"
                    onButtonClick={addNewProduct}
                    addStyle={{
                      border: "none",
                      background: "rgba(21, 115, 71, 1)",
                      color: "#ffffff",
                      height: "2.6rem",
                    }}
                    disabled={
                      activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfOutput.finishedDocument === true
                    }
                  />
                </div>
              </div>
            </>
            <div className="data-table-container">
              <DataTable value={productsDataTable}>
                <Column
                  body={nameOfProduct}
                  header={`${t("Naziv proizvoda")}:`}
                ></Column>
                <Column
                  body={nameOfCategory}
                  header={`${t("Tip proizvoda")}:`}
                ></Column>
                <Column field="lot" header={`${t("Lot")}:`}></Column>
                <Column field="amount" header={`${t("Količina")}:`}></Column>
                <Column field="measuringUnit" header={`${t("M.J")}:`}></Column>
                <Column
                  body={editActionBodyTemplate}
                  className="datatable-button-column"
                  header={`${t("Opcije")}:`}
                />
              </DataTable>
            </div>
            <RadioButtonInput
              id="foreignProduct"
              value={newDocumentOfOutput.foreignProduct}
              radioButtonValue1={t("DA")}
              radioButtonValue2={t("NE")}
              onValueChange={handleNewDocumentOfOutput}
              labelText={t("Tuđa roba na skladištenju")}
              control={control}
              errors={errors}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman &&
                newDocumentOfOutput?.status === ReceiveStatus.Finished
              }
            />
            {newDocumentOfOutput.foreignProduct === true && (
              <DropdownInput
                id="productOwner"
                value={newDocumentOfOutput.productOwner?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handleProductOwnerChange}
                options={partners}
                filter={true}
                labelText={`${t("Vlasnik robe")}`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
            )}
            <div className="note-field">
              <TextAreaInput
                id="note"
                value={newDocumentOfOutput.note as string}
                onValueChange={handleNewDocumentOfOutput}
                rows={1}
                cols={50}
                labelText={t("Napomena")}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                //rules={{ required: true }}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput.finishedDocument === true
                }
              />
            </div>
            <RadioButtonInput
              id="finishedDocument"
              value={newDocumentOfOutput.finishedDocument}
              radioButtonValue1={t("DA")}
              radioButtonValue2={t("NE")}
              onValueChange={handleNewDocumentOfOutput}
              labelText={t("Zaključena otpremnica")}
              control={control}
              errors={errors}
              disabled={
                (activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput.finishedDocument === true) ||
                activeUser?.role?.name === RoleType.Logistician
              }
            />
            <div className="footer-buttons">
              <ButtonInput
                labelText={t("Sačuvaj")}
                onButtonClick={handleSubmit(onSubmit, onError)}
                addStyle={addButtonSaveStyle}
                disabled={okButtonDisabled}
              />
              <ButtonInput
                labelText={t("Resetuj")}
                onButtonClick={handleResetForm}
                addStyle={addButtonResetStyle}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput.finishedDocument === true
                }
              />
            </div>
          </div>
          <div className="documents-preview">
            <PdfPreview
              pdfDefinition={pdfData}
              downloadDocument={downloadPdfDocument}
              printDocument={printPdfDocument}
              documentName={`Otpremnica_${
                newDocumentOfOutput.finishedDocumentId ?? ""
              }`}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
